import { FEATURES, type FeatureKey } from "./features"

export interface DocItem {
  label: string
  slug: string
  path: string
  description: string
  feature?: FeatureKey
  // Alternate content served when the Mining feature is off ("vanilla"
  // deploys) — same slug/URL, token-focused markdown.
  vanillaPath?: string
  vanillaDescription?: string
}

// WARNING: keep this a plain data-only array literal (string fields only).
// scripts/generate-static-docs.js extracts and evals it at build time —
// computed expressions or references inside the array would break the build.
export const docs: DocItem[] = [
  { label: "Overview", slug: "overview", path: "/assets/docs/overview.md", description: "Overview of the Prediction Mining platform and its core concepts.", vanillaPath: "/assets/docs/overview-vanilla.md", vanillaDescription: "Overview of the WELEPHANT token — how to get it, stake it, and bridge it." },
  { label: "Intro", slug: "intro", path: "/assets/docs/intro.md", description: "Introduction to Prediction Mining - get started with decentralized gaming.", feature: "mining" },
  { label: "Manifesto", slug: "manifesto", path: "/assets/docs/manifesto.md", description: "The WELEPHANT Manifesto — protocol foundation, architecture, economics, and the escape hatch thesis.", vanillaPath: "/assets/docs/manifesto-vanilla.md" },
  { label: "Tokenomics", slug: "tokenomics", path: "/assets/docs/tokenomics.md", description: "WELEPHANT tokenomics - distribution, supply, and economic model." },
  { label: "Mining", slug: "mining", path: "/assets/docs/mining.md", description: "Learn how mining works in the Prediction Mining game.", feature: "mining" },
  { label: "Mining Calculator", slug: "mining-calculator", path: "/assets/docs/mining-calculator.md", description: "Economic breakdown of mining profitability and the WELEPHANT flywheel.", feature: "mining" },
  { label: "Streamer", slug: "streamer", path: "/assets/docs/streamer.md", description: "Stable Stream — DCA BNB into stWELEPHANT with built-in affiliate rewards.", feature: "streams" },
  { label: "Staking", slug: "staking", path: "/assets/docs/staking.md", description: "Stake your tokens to earn rewards and participate in governance." },
  { label: "Bridging", slug: "bridging", path: "/assets/docs/bridging.md", description: "Bridge assets across chains using Wormhole technology." },
  { label: "Architecture", slug: "architecture", path: "/assets/docs/architecture.md", description: "Technical architecture and smart contract design." },
  { label: "Audit", slug: "audit", path: "/assets/docs/audit.md", description: "Security audits and code verification details." },
  { label: "Brand Guide", slug: "brand", path: "/assets/docs/brand.md", description: "Logo, colors, typography, and voice guidelines for the WELEPHANT brand." },
  { label: "Pitch Decks", slug: "pitch", path: "/assets/docs/pitch.md", description: "Presentation decks for players, miners, and investors.", vanillaPath: "/assets/docs/pitch-vanilla.md", vanillaDescription: "Presentation decks for holders and investors." },
  { label: "FAQ", slug: "faq", path: "/assets/docs/faq.md", description: "Frequently asked questions about Prediction Mining.", vanillaPath: "/assets/docs/faq-vanilla.md", vanillaDescription: "Frequently asked questions about the WELEPHANT token, staking, and bridging." },
  { label: "Links", slug: "links", path: "/assets/docs/links.md", description: "Official links, social media, and community resources.", vanillaPath: "/assets/docs/links-vanilla.md" },
]

// Docs visible under the current feature flags, with vanilla content
// substituted when Mining is off. Consumers should use this instead of
// `docs` so flagged-off features disappear from navigation.
export const visibleDocs = docs
  .filter((d) => !d.feature || FEATURES[d.feature])
  .map((d) =>
    !FEATURES.mining && d.vanillaPath
      ? { ...d, path: d.vanillaPath, description: d.vanillaDescription ?? d.description }
      : d
  )

export function getDocBySlug(slug: string): DocItem | undefined {
  return visibleDocs.find((d) => d.slug === slug)
}

// Navigation helpers
export function getDocIndex(doc: DocItem): number {
  return visibleDocs.findIndex((d) => d.label === doc.label)
}

export function getPrevDoc(doc: DocItem): DocItem | null {
  const index = getDocIndex(doc)
  return index > 0 ? visibleDocs[index - 1] : null
}

export function getNextDoc(doc: DocItem): DocItem | null {
  const index = getDocIndex(doc)
  return index < visibleDocs.length - 1 ? visibleDocs[index + 1] : null
}
